<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import Header from './component/header.vue'

const route   = useRoute()
const cycleId = route.params.id

const loading  = ref(false)
const cycle    = ref(null)
const goals    = ref([])
const reviews  = ref([])
const activeTab = ref('overview')

const load = async () => {
  try {
    loading.value = true
    const [c, g, r] = await Promise.all([
      axios.get(`/hr/pms/cycles/${cycleId}`),
      axios.get(`/hr/pms/goals`, { params: { cycle_id: cycleId, per_page: 100 } }),
      axios.get(`/hr/pms/reviews`, { params: { cycle_id: cycleId, per_page: 100 } }),
    ])
    cycle.value   = c.data.data
    goals.value   = g.data.data?.data ?? g.data.data ?? []
    reviews.value = r.data.data?.data ?? r.data.data ?? []
  } catch (e) { console.error(e) } finally { loading.value = false }
}

onMounted(load)

const statusClass = (s) => ({
  draft: 'badge--grey', active: 'badge--green',
  closed: 'badge--blue', submitted: 'badge--orange',
  approved: 'badge--green', pending: 'badge--orange',
}[s] ?? '')

const fmt = (v) => v ?? '—'
</script>

<template>
  <div>
    <Header />
    <div class="page">
      <div v-if="loading" class="empty">Loading…</div>
      <template v-else-if="cycle">
        <div class="cycle-hero">
          <div>
            <h2 class="page-title">{{ cycle.title }}</h2>
            <div class="cycle-meta">
              {{ cycle.review_type }} · {{ cycle.start_date }} → {{ cycle.end_date }}
            </div>
          </div>
          <span :class="['badge', statusClass(cycle.status)]">{{ cycle.status }}</span>
        </div>

        <div class="tab-bar">
          <button :class="['tab', activeTab === 'overview' ? 'tab--active' : '']" @click="activeTab = 'overview'">Overview</button>
          <button :class="['tab', activeTab === 'goals' ? 'tab--active' : '']" @click="activeTab = 'goals'">
            Goals ({{ goals.length }})
          </button>
          <button :class="['tab', activeTab === 'reviews' ? 'tab--active' : '']" @click="activeTab = 'reviews'">
            Reviews ({{ reviews.length }})
          </button>
        </div>

        <!-- Overview -->
        <div v-if="activeTab === 'overview'" class="tab-content">
          <div class="stats-row">
            <div class="stat-card">
              <div class="stat-label">Total Goals</div>
              <div class="stat-val">{{ goals.length }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Reviews</div>
              <div class="stat-val">{{ reviews.length }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Approved Reviews</div>
              <div class="stat-val">{{ reviews.filter(r => r.status === 'approved').length }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Pending Reviews</div>
              <div class="stat-val">{{ reviews.filter(r => r.status === 'pending' || r.status === 'submitted').length }}</div>
            </div>
          </div>
          <div v-if="cycle.description" class="desc-box">
            <strong>Description:</strong> {{ cycle.description }}
          </div>
        </div>

        <!-- Goals tab -->
        <div v-if="activeTab === 'goals'" class="tab-content">
          <div v-if="!goals.length" class="empty">No goals set for this cycle.</div>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Goal</th>
                <th>Weight</th>
                <th>Target</th>
                <th>Progress</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in goals" :key="g.id">
                <td>{{ g.employee?.full_name ?? '—' }}</td>
                <td>{{ g.title }}</td>
                <td>{{ g.weight ?? '—' }}%</td>
                <td>{{ fmt(g.target_value) }}</td>
                <td>{{ fmt(g.actual_value) }}</td>
                <td><span :class="['badge', statusClass(g.status)]">{{ g.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Reviews tab -->
        <div v-if="activeTab === 'reviews'" class="tab-content">
          <div v-if="!reviews.length" class="empty">No reviews for this cycle.</div>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Reviewer</th>
                <th>Overall Score</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in reviews" :key="r.id">
                <td>{{ r.employee?.full_name ?? '—' }}</td>
                <td>{{ r.reviewer?.full_name ?? '—' }}</td>
                <td>{{ r.overall_score ?? '—' }}</td>
                <td><span :class="['badge', statusClass(r.status)]">{{ r.status }}</span></td>
                <td>
                  <router-link :to="$url(`/hr/performance/reviews/${r.id}`)" class="btn-link">View</router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </template>
      <div v-else class="empty">Cycle not found.</div>
    </div>
  </div>
</template>

<style scoped>
.page { padding: 0 24px 40px; }
.cycle-hero { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 12px; }
.page-title { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0 0 4px; }
.cycle-meta { font-size: 13px; color: #888; text-transform: capitalize; }
.empty { text-align: center; color: #888; padding: 60px; font-size: 14px; }
.tab-bar { display: flex; gap: 4px; margin-bottom: 20px; border-bottom: 1px solid #eaf0fb; }
.tab { padding: 10px 18px; border: none; background: none; font-size: 13.5px; font-weight: 500; color: #7a90b0; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.tab--active { color: #004AAD; border-bottom-color: #004AAD; font-weight: 600; }
.tab-content { }
.stats-row { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; margin-bottom: 20px; }
.stat-card { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 16px 18px; }
.stat-label { font-size: 12px; color: #888; margin-bottom: 6px; }
.stat-val   { font-size: 28px; font-weight: 700; color: #1a2a45; }
.desc-box { background: #f7f9fc; border-radius: 8px; padding: 14px 18px; font-size: 13.5px; color: #445; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; background: #fff; border-radius: 10px; overflow: hidden; border: 1px solid #eaf0fb; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--grey   { background: #f0f3fa; color: #556; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--blue   { background: #cfe2ff; color: #084298; }
.badge--orange { background: #fff3cd; color: #856404; }
.btn-link { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0; text-decoration: none; }
</style>
